/**
 * Pad schemes: what the bumpers, the left trigger, and the right stick's
 * vertical axis do on top of the base mapping (left stick moves, and with
 * `stickActions` up jumps and down tucks; right stick x tilts; right trigger
 * reaches and grips; the face buttons alias jump, tuck, reach, and pull).
 * Selected by `tuning.padScheme` (F1 panel, `?scheme=N`); `docs/controls.md`
 * is the study behind them.
 */

export type ButtonAction = "none" | "jump" | "tuck" | "pull";

export interface PadScheme {
  name: string;
  lb: ButtonAction;
  rb: ButtonAction;
  /**
   * The left trigger pulls (the design), tucks (a held, analog crouch), or, held, turns the left stick
   * into the torso: its horizontal axis rotates the body and locomotion waits (the owner's pick of
   * 2026-09-18, so a flip is Jump, Tuck, then the left hand rotating).
   */
  lt: "pull" | "tuck" | "rotate";
  /** Right stick up past the action threshold reaches by its deflection, or nothing. */
  rsUp: "none" | "reach";
  /** Right stick down past the action threshold tucks, pulls by its deflection, or nothing. */
  rsDown: "none" | "tuck" | "pull";
  /** Whether the left stick's own up and down still jump and tuck (with `stickActions` on). */
  stickUp: "jump" | "none";
  stickDown: "tuck" | "none";
}

export const PAD_SCHEMES: readonly PadScheme[] = [
  {
    name: "design (bumpers unused)",
    lb: "none",
    rb: "none",
    lt: "pull",
    rsUp: "none",
    rsDown: "none",
    stickUp: "jump",
    stickDown: "tuck",
  },
  {
    name: "bumper jumper (LB jump, RB tuck)",
    lb: "jump",
    rb: "tuck",
    lt: "pull",
    rsUp: "none",
    rsDown: "none",
    stickUp: "jump",
    stickDown: "tuck",
  },
  {
    name: "bumper legs (LB tuck, RB jump)",
    lb: "tuck",
    rb: "jump",
    lt: "pull",
    rsUp: "none",
    rsDown: "none",
    stickUp: "jump",
    stickDown: "tuck",
  },
  {
    name: "stir (scheme 1 plus right stick up reaches, down tucks)",
    lb: "jump",
    rb: "tuck",
    lt: "pull",
    rsUp: "reach",
    rsDown: "tuck",
    stickUp: "jump",
    stickDown: "tuck",
  },
  {
    name: "body halves (left hand legs: LB jump, LT tuck; right hand arms: stick up reaches, down pulls; RB pull)",
    lb: "jump",
    rb: "pull",
    lt: "tuck",
    rsUp: "reach",
    rsDown: "pull",
    stickUp: "jump",
    stickDown: "tuck",
  },
  {
    name: "owner 2026-09-14 (LB jump, LT tuck, right stick down pulls, RB pull; no stick reach)",
    lb: "jump",
    rb: "pull",
    lt: "tuck",
    rsUp: "none",
    rsDown: "pull",
    stickUp: "jump",
    stickDown: "tuck",
  },
  {
    name: "owner 2026-09-18 (B is the only crouch; LT held turns the left stick into the torso)",
    lb: "jump",
    rb: "pull",
    lt: "rotate",
    rsUp: "none",
    rsDown: "pull",
    stickUp: "jump",
    stickDown: "none",
  },
];

/**
 * The default pad scheme: the owner's pick of 2026-09-18. The right face button is the only crouch,
 * and holding the left trigger hands the left stick to the torso, so a backflip is Jump, B, then the
 * left hand rotating the body, and a flight into a bar is positioned the same way.
 */
export const DEFAULT_PAD_SCHEME = 6;

/** The scheme for a tuning value; out-of-range values fall back to the default scheme. */
export function padScheme(index: number): PadScheme {
  const i = Math.round(index);
  return PAD_SCHEMES[i >= 0 && i < PAD_SCHEMES.length ? i : DEFAULT_PAD_SCHEME]!;
}
